import { Button } from '@components/core-ui/form-elements/buttons/Button';
import LoadingIcon from '@components/icons/Loading.svg';
import { cn } from '@helpers/cn';
import { useAPI } from '@providers/useAPI';
import {
  SubscriptionsService,
  type GetBillingAccountResponseSubscriptionDto,
  type PrismaOrganizationDto,
} from 'app/API';
import { useRetrySubscriptionPayment } from 'app/hooks/useRetrySubscriptionPayment';
import { isExternallyManagedOrganization } from 'app/helpers/isExternallyManagedOrganization';

type TProps = {
  subscription: GetBillingAccountResponseSubscriptionDto;
  managementSource: PrismaOrganizationDto['managementSource'];
  reload: () => void;
  className?: string;
};

export const PaymentFailedCard = ({ className, managementSource, reload, subscription }: TProps) => {
  const { call } = useAPI();

  const retry = () =>
    call(
      () =>
        SubscriptionsService.adminRetryPayment({
          baId: subscription.billingAccountId,
          subscriptionId: subscription.id,
        }),
      { dontRedirectOnError: true },
    );

  const { isPending, retryPayment } = useRetrySubscriptionPayment({ onPaid: reload, retry });

  const canRetry = !isExternallyManagedOrganization(managementSource) && subscription.status !== 'incomplete_expired';

  const onRetryClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    retryPayment();
  };

  return (
    <div
      className={cn(
        'flex min-h-[220px] w-[230px] flex-col rounded-[10px] bg-[var(--color-surface)] p-5 shadow-[0_8px_10px_var(--color-shadow)]',
        className,
      )}
    >
      <h2 className="m-0 text-center text-[18px] font-medium leading-[150%] text-[var(--color-text-muted)]">
        Payment Failed
      </h2>
      <p className="m-0 pt-2.5 text-sm leading-[150%] tracking-[0.42px] text-[var(--color-text-muted)]">
        Your AutoScript One subscription is temporarily disabled. Please try again or use another payment method.
      </p>
      <p className="m-0 pt-2.5 text-sm leading-[150%] tracking-[0.42px] text-[var(--color-text-muted)]">
        For assistance, please contact{' '}
        <a className="font-bold" href="mailto:support@voicescript.ai" onClick={(event) => event.stopPropagation()}>
          support@voicescript.ai
        </a>
        .
      </p>
      {canRetry ? (
        <Button
          aria-busy={isPending}
          className="btn--md mt-auto self-stretch"
          disabled={isPending}
          onClick={onRetryClick}
        >
          {isPending ? <LoadingIcon className="h-3 w-3 animate-spin" /> : null}
          Retry
        </Button>
      ) : null}
    </div>
  );
};
